import { state } from '../state/app-state';

/* ─────────────────────────────────────────────
   Storage / persistence — localStorage I/O
   (token / keypair / session / cadence / device-history / mine-markers)
   ───────────────────────────────────────────── */
export const LS_TOKEN_KEY = 'trail.device.token';
// History of every device created from this browser. Used so that picking
// the same animals after a reload restores the original token instead of
// silently creating a new one (the backend's collision-retry would mint a
// different attempt-salted token, and the user's pings would be stranded
// under the old token in D1).
export const LS_DEVICES_KEY = 'trail.devices';
// Per-token ed25519 keypair. priv = base64url pkcs8, pub = base64url raw.
// Stored separately from LS_DEVICES_KEY so each device's signing material
// lives at its own key and can be wiped in isolation. The `.v2.` namespace
// invalidates any cached keys held against pre-reshape tokens (the 0003
// migration wiped server-side devices and pings).
export const LS_KEYS_PREFIX = 'trail.keys.v2.';
// Per-token active server-tracked session. `{id, started_at}` while a
// recording session is open; absent when not recording.
export const LS_SESSION_PREFIX = 'trail.session.';
// Log cadence (seconds between recorded fixes). Browser-global, not per-token,
// and persisted so it survives reloads (mirrors Android's KvStore-backed
// setting). The record strip and the Settings panel both read/write it.
export const LS_CADENCE_KEY = 'trail.cadence';
export const CADENCE_CHOICES = [5, 10, 30, 60, 300];

export function loadStoredToken(){
  try { return localStorage.getItem(LS_TOKEN_KEY); } catch { return null; }
}
export function saveToken(tok: any){ try { localStorage.setItem(LS_TOKEN_KEY, tok); } catch {} }
export function clearStoredToken(){ try { localStorage.removeItem(LS_TOKEN_KEY); } catch {} }

export function keysKey(token: string){ return LS_KEYS_PREFIX + token; }
export function loadKeypair(token: string){
  try {
    const raw = localStorage.getItem(keysKey(token));
    if(!raw) return null;
    const parsed = JSON.parse(raw);
    if(!parsed || typeof parsed.priv !== 'string' || typeof parsed.pub !== 'string') return null;
    return parsed;
  } catch { return null; }
}
export function saveKeypair(token: string, kp: any){
  try { localStorage.setItem(keysKey(token), JSON.stringify({priv: kp.priv, pub: kp.pub})); } catch {}
}
export function clearKeypair(token: string){
  try { localStorage.removeItem(keysKey(token)); } catch {}
}

export function sessionKey(token: string){ return LS_SESSION_PREFIX + token; }
export function loadActiveSession(token: string){
  try {
    const raw = localStorage.getItem(sessionKey(token));
    if(!raw) return null;
    const parsed = JSON.parse(raw);
    if(!parsed || typeof parsed.id !== 'number' || typeof parsed.started_at !== 'number') return null;
    return parsed;
  } catch { return null; }
}
export function saveActiveSession(token: string, sess: any){
  try { localStorage.setItem(sessionKey(token), JSON.stringify({id: sess.id, started_at: sess.started_at})); } catch {}
}
export function clearActiveSession(token: string){
  try { localStorage.removeItem(sessionKey(token)); } catch {}
}

export function loadCadence(): number | null {
  try {
    const v = parseInt(localStorage.getItem(LS_CADENCE_KEY) || '', 10);
    return CADENCE_CHOICES.includes(v) ? v : null;
  } catch { return null; }
}
export function saveCadence(sec: number){ try { localStorage.setItem(LS_CADENCE_KEY, String(sec)); } catch {} }

// Single source of truth for the log cadence. The control lives only in
// Settings (mirroring Android); the record strip shows the value as a readout
// via updateRecordUi(). Updates state and persists.
// NOTE: the original setCadence also called renderSettingsPanel() +
// updateRecordUi(); those render calls stay at the UI caller side (the
// Settings-panel click handler and the boot cadence restore) per the layer
// rule that storage must not call into render/ui.
export function setCadence(sec: number){
  if(!CADENCE_CHOICES.includes(sec)) return;
  state.cadenceSec = sec;
  saveCadence(sec);
}

export function loadDeviceHistory(){
  try { return JSON.parse(localStorage.getItem(LS_DEVICES_KEY) || '[]'); }
  catch { return []; }
}
export function saveDeviceHistory(devices: any[]){
  try { localStorage.setItem(LS_DEVICES_KEY, JSON.stringify(devices)); } catch {}
}
export function comboKey(animals: string[], group: string[] | null){
  const a = (animals || []).join('-');
  const g = (group && group.length === 2) ? group.join('-') : '';
  return g + '|' + a;
}
export function findInHistory(animals: string[], group: string[] | null){
  const key = comboKey(animals, group);
  return loadDeviceHistory().find((d: any) => comboKey(d.animals, d.group) === key) || null;
}
export function rememberDevice(device: any){
  const history = loadDeviceHistory();
  if(history.find((d: any) => d.token === device.token)) return;
  history.push(device);
  saveDeviceHistory(history);
}
export function forgetFromHistory(token: string){
  saveDeviceHistory(loadDeviceHistory().filter((d: any) => d.token !== token));
}
// A "pending" device was minted client-side during an API outage: it has a
// local keypair but no server row yet, so its pings can't sync. We carry the
// flag in the history entry and clear it once ensureRegistered() succeeds.
export function isPendingDevice(token: string){
  const d = loadDeviceHistory().find((x: any) => x.token === token);
  return !!(d && d.pending);
}
export function setPendingFlag(token: string, pending: boolean){
  const history = loadDeviceHistory();
  const d = history.find((x: any) => x.token === token);
  if(!d) return;
  if(pending) d.pending = true; else delete d.pending;
  saveDeviceHistory(history);
}
// Re-point a history entry from a client-derived token to the one the server
// actually issued (only differs on a deterministic-token collision).
export function retokenHistory(oldToken: string, newDevice: any){
  const history = loadDeviceHistory().filter((d: any) => d.token !== oldToken && d.token !== newDevice.token);
  history.push(newDevice);
  saveDeviceHistory(history);
}

export function mineMarkersKey(path: string){ return `locus.team.mineMarkers.${path}`; }
export function loadMineMarkers(path: string): Set<number> {
  try {
    const raw = localStorage.getItem(mineMarkersKey(path));
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((n: any) => typeof n === 'number') : []);
  } catch { return new Set(); }
}
export function saveMineMarkers(path: string, ids: Set<number>){
  try { localStorage.setItem(mineMarkersKey(path), JSON.stringify([...ids])); } catch { /* quota — ignore */ }
}
export function addMineMarker(path: string, id: number){ const s = loadMineMarkers(path); s.add(id); saveMineMarkers(path, s); }
export function removeMineMarker(path: string, id: number){ const s = loadMineMarkers(path); if(s.delete(id)) saveMineMarkers(path, s); }
